import { useState, useEffect, useCallback, useMemo } from "react";
import { useApi } from "./useApi";

export const useColumnMapping = (provider, category, tenantId) => {
  const { getColumnMapping, loading, error } = useApi();
  const [columnMapping, setColumnMapping] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasAuthError, setHasAuthError] = useState(false);

  const loadColumnMapping = useCallback(
    async (prov, cat, tenant) => {
      try {
        console.log("Loading column mapping:", {
          provider: prov,
          category: cat,
          tenantId: tenant,
        });
        setIsLoading(true);
        const response = await getColumnMapping(prov, cat, tenant, "flat");
        console.log("Column mapping response:", response);

        // Extract mapping_list from response.data.mapping_list
        if (response && response.success && response.data) {
          // Check if mapping_list exists and is an array
          if (
            response.data.mapping_list &&
            Array.isArray(response.data.mapping_list)
          ) {
            // Enrich mapping items with provider/category/tenant_id if missing
            const enrichedMappingList = response.data.mapping_list.map(
              (item) => ({
                ...item,
                provider: item.provider || prov || "",
                category: item.category || cat || "",
                tenant_id: item.tenant_id || tenant || "",
              })
            );
            console.log(
              "Setting column mapping from mapping_list:",
              enrichedMappingList.length,
              "items"
            );
            setColumnMapping(enrichedMappingList);
          } else if (
            response.data.mappings &&
            Array.isArray(response.data.mappings)
          ) {
            // Fallback to mappings array if mapping_list doesn't exist
            console.log(
              "Setting column mapping from mappings array:",
              response.data.mappings.length,
              "items"
            );
            const enrichedMappings = response.data.mappings.map((item) => ({
              ...item,
              provider: item.provider || prov || "",
              category: item.category || cat || "",
              tenant_id: item.tenant_id || tenant || "",
            }));
            setColumnMapping(enrichedMappings);
          } else if (Array.isArray(response.data)) {
            // Direct array in response.data
            console.log(
              "Setting column mapping from response.data array:",
              response.data.length,
              "items"
            );
            setColumnMapping(response.data);
          } else {
            console.warn(
              "No mapping_list or mappings array found in response.data",
              {
                hasMappingList: !!response.data.mapping_list,
                hasMappings: !!response.data.mappings,
                dataKeys: Object.keys(response.data || {}),
              }
            );
            setColumnMapping(null);
          }
        } else if (response && Array.isArray(response)) {
          console.log(
            "Setting column mapping from array response:",
            response.length,
            "items"
          );
          setColumnMapping(response);
        } else if (response && response.data && Array.isArray(response.data)) {
          console.log(
            "Setting column mapping from response.data array:",
            response.data.length,
            "items"
          );
          setColumnMapping(response.data);
        } else {
          console.warn("No valid column mapping structure in response", {
            hasResponse: !!response,
            hasSuccess: response?.success,
            hasData: !!response?.data,
            responseKeys: response ? Object.keys(response) : [],
          });
          setColumnMapping(null);
        }
      } catch (err) {
        console.error("Failed to load column mapping:", err);
        setColumnMapping(null);
        
        // Check if it's an authentication error
        if (err.status === 401 || err.isAuthError) {
          console.log("Authentication error detected in column mapping - stopping requests");
          setHasAuthError(true);
        }
      } finally {
        setIsLoading(false);
      }
    },
    [getColumnMapping]
  );

  useEffect(() => {
    // Don't make requests if there's an auth error
    if (hasAuthError) {
      console.log("⛔ Skipping column mapping load due to authentication error");
      return;
    }
    
    console.log("useColumnMapping effect triggered:", {
      provider,
      category,
      tenantId,
    });

    if (tenantId) {
      // Load mapping for specific provider and category
      // Use actual provider/category values or "default" as fallback
      const prov = provider && provider !== "" ? provider : "default";
      const cat = category && category !== "" ? category : "default";
      loadColumnMapping(prov, cat, tenantId);
    }
  }, [loadColumnMapping, provider, category, tenantId, hasAuthError]);

  // Create a mapping lookup: input_column_name -> inventory_column_name
  const fieldMappingLookup = useMemo(() => {
    if (!columnMapping || !Array.isArray(columnMapping)) {
      return {};
    }

    const lookup = {};
    columnMapping.forEach((mapping) => {
      if (mapping.input_column_name && mapping.inventory_column_name) {
        lookup[mapping.input_column_name] = mapping.inventory_column_name;
      }
    });

    console.log("Field mapping lookup created:", lookup);
    return lookup;
  }, [columnMapping]);

  // Get list of input column names for the current provider/category
  const getInputColumns = useCallback(() => {
    if (!columnMapping || !Array.isArray(columnMapping)) {
      return [];
    }

    // Filter by current provider and category
    // Use actual provider/category or "default" for matching
    const prov = provider && provider !== "" ? provider : "default";
    const cat = category && category !== "" ? category : "default";

    const filtered = columnMapping.filter((m) => {
      const matchesProvider =
        !provider || m.provider === prov || m.provider === "default";
      const matchesCategory =
        !category || m.category === cat || m.category === "default";
      return matchesProvider && matchesCategory;
    });

    // Extract unique input column names in order they appear in the mapping
    const columns = [];
    const seen = new Set();
    filtered.forEach((m) => {
      if (m.input_column_name && !seen.has(m.input_column_name)) {
        columns.push(m.input_column_name);
        seen.add(m.input_column_name);
      }
    });

    console.log("getInputColumns result:", {
      provider: prov,
      category: cat,
      totalMappings: columnMapping.length,
      filteredCount: filtered.length,
      columns,
    });

    return columns;
  }, [columnMapping, provider, category]);

  // Map data from input column names to inventory column names
  const mapDataFields = useCallback(
    (dataItem) => {
      if (!dataItem || !fieldMappingLookup) {
        return dataItem;
      }

      const mapped = { ...dataItem };
      Object.keys(fieldMappingLookup).forEach((inputColumn) => {
        const inventoryColumn = fieldMappingLookup[inputColumn];
        if (mapped[inputColumn] !== undefined && mapped[inputColumn] !== null) {
          // Copy value to inventory column name while keeping original
          mapped[inventoryColumn] = mapped[inputColumn];
        }
      });

      return mapped;
    },
    [fieldMappingLookup]
  );

  // Get list of inventory column names for the current provider/category
  const getInventoryColumns = useCallback(() => {
    if (!columnMapping || !Array.isArray(columnMapping)) {
      return [];
    }

    // Filter by current provider and category
    const prov = provider && provider !== "" ? provider : "default";
    const cat = category && category !== "" ? category : "default";

    const filtered = columnMapping.filter((m) => {
      const matchesProvider =
        !provider || m.provider === prov || m.provider === "default";
      const matchesCategory =
        !category || m.category === cat || m.category === "default";
      return matchesProvider && matchesCategory;
    });

    // Extract unique inventory column names in order they appear in the mapping
    const inventoryColumns = [];
    const seen = new Set();
    filtered.forEach((m) => {
      if (m.inventory_column_name && !seen.has(m.inventory_column_name)) {
        inventoryColumns.push(m.inventory_column_name);
        seen.add(m.inventory_column_name);
      }
    });

    console.log("getInventoryColumns result:", {
      provider: prov,
      category: cat,
      totalMappings: columnMapping.length,
      filteredCount: filtered.length,
      inventoryColumns,
    });

    return inventoryColumns;
  }, [columnMapping, provider, category]);

  const getColumnsForCategoryProvider = useCallback(
    (cat, prov) => {
      // Return input columns for the specified category and provider
      const columns = getInputColumns();

      // If no columns from mapping, return empty array
      if (columns.length === 0) {
        console.warn(
          "No column mapping found for category and provider",
          cat,
          prov
        );
      }

      return columns;
    },
    [getInputColumns]
  );

  return {
    columnMapping,
    fieldMappingLookup, // Lookup object: { "NIT": "circuit_id", ... }
    getColumnsForCategoryProvider,
    getInputColumns,
    getInventoryColumns, // Returns inventory_column_name values
    mapDataFields,
    isLoading: isLoading || loading,
    error,
    refreshMapping: loadColumnMapping,
  };
};
